import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PolicyDefinition, PolicyDefinitionInput, IdResponse, QuerySpec } from '../model';
import { ManagementRouteService } from '../common/managementRoute.service';
import { MANAGEMENT_API_CONTEXT } from '../variables';

@Injectable({
  providedIn: 'root',
})
export class PolicyService {
  constructor(
    private readonly http: HttpClient,
    private readonly managementRoute: ManagementRouteService
  ) {}

  // ── Overloads kept for backward-compat ───────────────────────────────────────

  public createPolicy(input: PolicyDefinitionInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<IdResponse>;
  public createPolicy(input: PolicyDefinitionInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<IdResponse>>;
  public createPolicy(input: PolicyDefinitionInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<IdResponse>>;
  public createPolicy(input: PolicyDefinitionInput): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<IdResponse>(`${url}/policydefinitions`, this.toJsonLdPolicyInput(input));
  }

  public deletePolicy(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public deletePolicy(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public deletePolicy(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public deletePolicy(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling deletePolicy.');
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.delete(`${url}/policydefinitions/${encodeURIComponent(id)}`);
  }

  public updatePolicy(id: string, input: PolicyDefinitionInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public updatePolicy(id: string, input: PolicyDefinitionInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public updatePolicy(id: string, input: PolicyDefinitionInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public updatePolicy(id: string, input: PolicyDefinitionInput): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling updatePolicy.');
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.put(`${url}/policydefinitions/${encodeURIComponent(id)}`, this.toJsonLdPolicyInput(input));
  }

  public getPolicy(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<PolicyDefinition>;
  public getPolicy(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<PolicyDefinition>>;
  public getPolicy(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<PolicyDefinition>>;
  public getPolicy(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling getPolicy.');
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<any>(`${url}/policydefinitions/${encodeURIComponent(id)}`).pipe(
      map(item => this.toPolicyInstance(item))
    );
  }

  public queryAllPolicies(querySpec?: QuerySpec, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<Array<PolicyDefinition>>;
  public queryAllPolicies(querySpec?: QuerySpec, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<Array<PolicyDefinition>>>;
  public queryAllPolicies(querySpec?: QuerySpec, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<Array<PolicyDefinition>>>;
  public queryAllPolicies(querySpec?: QuerySpec): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<any[]>(`${url}/policydefinitions/request`, this.toJsonLdQuerySpec(querySpec)).pipe(
      map(items => items.map(item => this.toPolicyInstance(item)))
    );
  }

  private toJsonLdPolicyInput(input: PolicyDefinitionInput): Record<string, any> {
    const policy = { ...(input as any).policy };

    for (const key of ['permission', 'prohibition', 'obligation'] as const) {
      if (Array.isArray(policy[key]) && policy[key].length === 0) {
        delete policy[key];
      }
    }
    return {
      '@context': ['https://w3id.org/edc/connector/management/v2'],
      '@type': 'PolicyDefinition',
      ...(input as any),
      policy,
    };
  }

  private toPolicyInstance(raw: any): any {
    const rawPolicy = raw['policy'] ?? {};
    const policy = {
      ...rawPolicy,
      // ODRL uses singular; template expects plural
      permissions:  rawPolicy['permissions']  ?? rawPolicy['permission']  ?? [],
      prohibitions: rawPolicy['prohibitions'] ?? rawPolicy['prohibition'] ?? [],
      obligations:  rawPolicy['obligations']  ?? rawPolicy['obligation']  ?? [],
      assigner:     rawPolicy['assigner']     ?? rawPolicy['odrl:assigner']  ?? null,
      assignee:     rawPolicy['assignee']     ?? rawPolicy['odrl:assignee']  ?? null,
    };
    return {
      ...raw,
      id:     raw['@id'] ?? raw['id'],
      '@id':  raw['@id'] ?? raw['id'],
      policy,
    };
  }

  private toJsonLdQuerySpec(querySpec?: QuerySpec): Record<string, any> {
    const body: Record<string, any> = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'QuerySpec',
      'offset': querySpec?.offset ?? 0,
      'limit':  querySpec?.limit  ?? 50,
    };
    if (querySpec?.sortOrder)  body['sortOrder']  = querySpec.sortOrder;
    if (querySpec?.sortField)  body['sortField']  = querySpec.sortField;
    if (querySpec?.filterExpression?.length) body['filterExpression'] = querySpec.filterExpression;
    return body;
  }
}
